import { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    alpha,
    useTheme,
    Divider
} from '@mui/material'
import { Warning, Visibility, ArrowForward } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import { useSubmissionStore } from '@store/submissionStore'
import api from '@api/axios'

const SESSION_KEY = 'readPendingAlertShown'

const ReadPendingAlert = () => {
    const [open, setOpen] = useState(false)
    const [pendingSubmissions, setPendingSubmissions] = useState([])
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { setFilter, resetFilters } = useSubmissionStore()
    const navigate = useNavigate()
    const theme = useTheme()

    const isFormManager = user?.user_type === 'form_manager'
    const isDashboard = user?.user_type === 'dashboard'

    useEffect(() => {
        // Only show for form_manager and dashboard users
        if (!isFormManager && !isDashboard) return

        // Only show once per session
        if (sessionStorage.getItem(SESSION_KEY)) return

        const fetchPending = async () => {
            try {
                const response = await api.get('/submissions?per_page=1000')
                const submissions = response?.data || response || []

                if (!Array.isArray(submissions)) return

                const pending = submissions.filter(
                    s => s.status === 'read' && !s.archived
                )

                if (pending.length > 0) {
                    setPendingSubmissions(pending)
                    setOpen(true)
                }

                // Mark as shown for this session
                sessionStorage.setItem(SESSION_KEY, 'true')
            } catch (err) {
                console.error('Failed to fetch pending submissions:', err)
            }
        }

        fetchPending()
    }, [isFormManager, isDashboard])

    const handleDismiss = () => {
        setOpen(false)
    }

    const handleViewAll = () => {
        setOpen(false)
        resetFilters()
        setFilter('status', 'read')
        setFilter('archived', false)
        navigate('/submissions')
    }

    const handleViewSubmission = (submissionId) => {
        setOpen(false)
        navigate(`/submissions/${submissionId}`)
    }

    if (!open || pendingSubmissions.length === 0) return null

    return (
        <Dialog
            open={open}
            onClose={handleDismiss}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
                    borderBottom: `2px solid ${theme.palette.error.main}`,
                    pb: 2,
                }}
            >
                <Warning sx={{ color: theme.palette.error.main, fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700} color="error.main">
                    {t('pendingAlert.title')}
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    {t('pendingAlert.message').replace('{count}', pendingSubmissions.length)}
                </Typography>

                <List
                    sx={{
                        maxHeight: 300,
                        overflow: 'auto',
                        bgcolor: alpha(theme.palette.error.main, 0.03),
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
                    }}
                >
                    {pendingSubmissions.slice(0, 10).map((sub, index) => (
                        <Box key={sub.id}>
                            {index > 0 && <Divider />}
                            <ListItem
                                sx={{
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: alpha(theme.palette.error.main, 0.08),
                                    },
                                    transition: 'background 0.2s',
                                }}
                                onClick={() => handleViewSubmission(sub.id)}
                            >
                                <ListItemIcon sx={{ minWidth: 36 }}>
                                    <Visibility fontSize="small" color="error" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" fontWeight={600}>
                                            {sub.submission_id}
                                        </Typography>
                                    }
                                    secondary={
                                        sub.created_at
                                            ? new Date(sub.created_at).toLocaleDateString(
                                                language === 'ar' ? 'ar-SA' : 'en-US',
                                                { year: 'numeric', month: 'short', day: 'numeric' }
                                            )
                                            : ''
                                    }
                                />
                                <Chip
                                    label={t('status.read')}
                                    color="error"
                                    size="small"
                                    sx={{ fontWeight: 500, borderRadius: '8px' }}
                                />
                                <ArrowForward
                                    fontSize="small"
                                    sx={{ ml: 1, color: 'text.secondary' }}
                                />
                            </ListItem>
                        </Box>
                    ))}
                </List>

                {pendingSubmissions.length > 10 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        +{pendingSubmissions.length - 10} more...
                    </Typography>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={handleDismiss} color="inherit" variant="outlined">
                    {t('pendingAlert.dismiss')}
                </Button>
                <Button
                    onClick={handleViewAll}
                    color="error"
                    variant="contained"
                    startIcon={<Visibility />}
                    sx={{
                        px: 3,
                        fontWeight: 600,
                        borderRadius: 2,
                    }}
                >
                    {t('pendingAlert.viewAll')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ReadPendingAlert
